import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SearchBar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const navigate = useNavigate();

    const searchItems = [
        { title: 'Home', path: '/', description: 'Main landing page' },
        { title: 'For Creators', path: '/creator', description: 'Creator tools and features' },
        { title: 'For Viewers', path: '/viewer', description: 'Viewer experience' },
        { title: 'For Hoisters', path: '/hoister', description: 'Hosting and earning' },
        { title: 'About Us', path: '/about', description: 'Our mission and values' },
        { title: 'Contact', path: '/contact', description: 'Get in touch with us' },
    ];

    const filteredItems = query
        ? searchItems.filter(
            (item) =>
                item.title.toLowerCase().includes(query.toLowerCase()) ||
                item.description.toLowerCase().includes(query.toLowerCase())
        )
        : searchItems;

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                setIsOpen(true);
            }
            if (e.key === 'Escape') {
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleSelect = (path) => {
        navigate(path);
        setIsOpen(false);
        setQuery('');
    };

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsOpen(false)}
                            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
                        />

                        {/* Search Modal */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -20 }}
                            className="fixed top-20 left-1/2 -translate-x-1/2 w-full max-w-2xl z-50 px-4"
                        >
                            <div className="glass-card p-0 overflow-hidden">
                                {/* Search Input */}
                                <div className="flex items-center px-4 py-4 border-b border-dark-border">
                                    <Search className="w-5 h-5 text-light-muted mr-3" />
                                    <input
                                        type="text"
                                        placeholder="Search pages..."
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        autoFocus
                                        className="flex-1 bg-transparent text-light placeholder-light-muted outline-none"
                                    />
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="text-light-muted hover:text-light ml-2"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Results */}
                                <div className="max-h-96 overflow-y-auto">
                                    {filteredItems.length > 0 ? (
                                        <div className="p-2">
                                            {filteredItems.map((item, index) => (
                                                <button
                                                    key={item.path}
                                                    onClick={() => handleSelect(item.path)}
                                                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-dark-lighter transition-colors group"
                                                >
                                                    <div className="font-medium text-light group-hover:text-primary transition-colors">
                                                        {item.title}
                                                    </div>
                                                    <div className="text-sm text-light-muted mt-1">
                                                        {item.description}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="p-8 text-center text-light-muted">
                                            No results found for "{query}"
                                        </div>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="px-4 py-3 border-t border-dark-border bg-dark-card/50 text-xs text-light-muted flex items-center justify-between">
                                    <span>Press ESC to close</span>
                                    <span>⌘K to open</span>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default SearchBar;
